/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React, { useCallback, useEffect, useId, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './Modal.scss';
import Button from '../Button';
import Input, { FormField } from '../Input';
import { Typography } from '../Typography';
import { FlexRow, FlexColumn } from '../GridsAndLayouts';
import { BubbleCheckIcon } from '../Icons/CheckCircleIcon';
import { CloseIcon } from '../Icons/CloseIcon';
import { ExclamationIcon } from '../Icons/ExclamationIcon';
import { AlertIcon } from '../Icons/AlertIcon';

const ModalBody = ({ children, className = '' }) => (
  <FlexColumn className={`ds-modal-body ${className}`.trim()}>{children}</FlexColumn>
);
ModalBody.displayName = 'ModalBody';

const ModalFooter = ({ children, className = '', centered = false, wideButtons = false, grayFooter = false }) => {
  const actionClasses = classNames(
    'ds-modal-actions',
    centered && 'center',
    wideButtons && 'wide',
    grayFooter && 'ds-modal-footer-gray',
    className
  );

  return (
    <FlexRow className={actionClasses} justify={centered ? 'center' : 'end'} fullWidth>
      {children}
    </FlexRow>
  );
};
ModalFooter.displayName = 'ModalFooter';

function renderLegacyDefaultIcon(modalType) {
  if (modalType === 'success') {
    return <BubbleCheckIcon size={55} />;
  }
  return <ExclamationIcon size={24} />;
}

function renderModuleBadgeIcon(modalType, customIcon) {
  if (customIcon !== undefined) return customIcon;
  if (modalType === 'success') {
    return <BubbleCheckIcon size={55} />;
  }
  return <ExclamationIcon size={28} strokeWidth={2.4} />;
}

/** Avoids nested ternaries in action rows (`no-nested-ternary`). */
function renderModalFooterButtonLabel(variant, text) {
  if (variant === 'legacy') {
    return <Typography variant="t15">{text}</Typography>;
  }
  return (
    <Typography variant="t15" component="span" className="ds-modal-button__label">
      {text}
    </Typography>
  );
}

/** When `boldText` is set, bolds the first case-insensitive match in the instruction string. */
function renderConfirmationInstructionLabel(instruction, boldText) {
  const trimmed = typeof instruction === 'string' ? instruction.trim() : instruction;
  if (!trimmed) return undefined;
  if (typeof trimmed !== 'string' || !boldText) return trimmed;

  const needle = String(boldText);
  const lowerInstruction = trimmed.toLowerCase();
  const matchIndex = lowerInstruction.indexOf(needle.toLowerCase());
  if (matchIndex === -1) return trimmed;

  const keyword = trimmed.slice(matchIndex, matchIndex + needle.length);
  const before = trimmed.slice(0, matchIndex);
  const after = trimmed.slice(matchIndex + needle.length);

  return (
    <>
      {before}
      <strong className="ds-modal-confirmation__keyword">{keyword}</strong>
      {after}
    </>
  );
}

const Modal = ({
  open = false,
  /** `legacy` (default) preserves the previous layout; use `module` or `dialog` for the new Figma shells. */
  variant = 'legacy',
  type = 'success',
  centered = false,
  title = '',
  description = '',
  primaryText,
  secondaryText,
  onPrimary,
  onSecondary,
  onDismiss,
  showClose = false,
  wideButtons = false,
  grayFooter = false,
  children,
  className = '',
  /** Merged onto the portal overlay (`.ds-modal-overlay`), e.g. for stacking above a high z-index drawer. */
  overlayClassName = '',
  id,
  primaryButtonId,
  secondaryButtonId,
  confirmationInputId,
  showIcon = true,
  icon,
  confirmationInput,
  loading = false,
  /** When `secondary`, primary action uses the same colors as the modal secondary button (neutral). */
  primaryButtonTone = 'primary',
  dangerBanner,
  onDangerBannerDismiss,
}) => {
  const titleId = useId();
  const dangerBannerTitleId = useId();
  const fallbackConfirmationFieldId = useId();
  const resolvedConfirmationInputId =
    confirmationInputId ?? (id ? `${id}-confirmation-input` : fallbackConfirmationFieldId);
  const resolvedPrimaryButtonId = primaryButtonId ?? (id ? `${id}-primary-btn` : undefined);
  const resolvedSecondaryButtonId = secondaryButtonId ?? (id ? `${id}-secondary-btn` : undefined);
  const resolvedCloseButtonId = id ? `${id}-close-btn` : undefined;
  const [confirmationValue, setConfirmationValue] = useState('');
  const composed = children != null;

  const requiresConfirmationMatch = Boolean(
    confirmationInput?.expectedValue != null && confirmationInput.expectedValue !== ''
  );

  const confirmationMatches = (() => {
    if (!requiresConfirmationMatch) return true;
    const expected = confirmationInput.expectedValue;
    const typed = confirmationValue.trim();
    if (confirmationInput.ignoreCase) {
      return typed.toLowerCase() === String(expected).toLowerCase();
    }
    return typed === expected;
  })();

  const primaryDisabled = Boolean(loading) || (requiresConfirmationMatch && !confirmationMatches);

  useEffect(() => {
    if (!open || !requiresConfirmationMatch) return;
    setConfirmationValue('');
  }, [open, requiresConfirmationMatch]);

  const handleDismiss = useCallback(() => {
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  const handleDangerBannerDismiss = useCallback(() => {
    if (onDangerBannerDismiss) onDangerBannerDismiss();
    else handleDismiss();
  }, [onDangerBannerDismiss, handleDismiss]);

  const handleSecondary = useCallback(() => {
    if (onSecondary) onSecondary();
    else handleDismiss();
  }, [onSecondary, handleDismiss]);

  const handleConfirmationKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      if (primaryDisabled) return;
      if (!onPrimary) return;
      event.preventDefault();
      onPrimary();
    },
    [primaryDisabled, onPrimary]
  );

  useEffect(() => {
    if (!open || !onDismiss) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onDismiss, handleDismiss]);

  if (!open) return null;

  const primaryUsesNeutralTone =
    primaryButtonTone === 'secondary' || (requiresConfirmationMatch && !confirmationMatches && type === 'danger');

  const modalClasses = classNames(
    'ds-modal',
    `ds-modal--${variant}`,
    `ds-modal-${type}`,
    variant === 'legacy' && centered && 'ds-modal-centered',
    showClose && 'ds-modal--has-close',
    composed && 'ds-modal--composed',
    className
  );

  const actionClasses = classNames(
    'ds-modal-actions',
    variant === 'legacy' && centered && 'center',
    variant === 'legacy' && wideButtons && 'wide',
    variant === 'legacy' && grayFooter && 'ds-modal-footer-gray',
    variant === 'module' && primaryText && secondaryText && 'ds-modal-actions--module-split',
    variant === 'module' && primaryText && !secondaryText && 'ds-modal-actions--module-full',
    variant === 'dialog' && 'ds-modal-actions--dialog'
  );

  const actionsJustify = (() => {
    if (variant === 'module' && primaryText && secondaryText) return 'between';
    if (variant === 'module' && primaryText && !secondaryText) return 'start';
    if (variant === 'legacy' && centered) return 'center';
    return 'end';
  })();
  const actionsAlign = variant === 'dialog' ? 'start' : 'stretch';

  let primaryActionVariant = 'positive';
  if (primaryUsesNeutralTone) {
    primaryActionVariant = 'secondary';
  } else if (type === 'danger') {
    primaryActionVariant = 'danger';
  }

  const renderActions = () => (
    <FlexRow className={actionClasses} justify={actionsJustify} align={actionsAlign} fullWidth>
      {secondaryText ? (
        <Button
          id={resolvedSecondaryButtonId}
          variant="secondary"
          size="base"
          type="button"
          className="ds-modal-button ds-modal-button-secondary"
          onClick={handleSecondary}
        >
          {renderModalFooterButtonLabel(variant, secondaryText)}
        </Button>
      ) : null}

      {primaryText ? (
        <Button
          id={resolvedPrimaryButtonId}
          variant={primaryActionVariant}
          size="base"
          type="button"
          className={classNames(
            'ds-modal-button',
            'ds-modal-button-primary',
            `ds-modal-button-${type}`,
            primaryUsesNeutralTone && 'ds-modal-button-primary--tone-secondary'
          )}
          onClick={onPrimary}
          disabled={primaryDisabled}
        >
          {renderModalFooterButtonLabel(variant, primaryText)}
        </Button>
      ) : null}
    </FlexRow>
  );

  const confirmationHasMismatch = requiresConfirmationMatch && confirmationValue.trim() !== '' && !confirmationMatches;

  const confirmationMismatchMessage = confirmationHasMismatch ? 'Does not match the required text.' : undefined;

  const confirmationField = requiresConfirmationMatch ? (
    <FormField
      id={resolvedConfirmationInputId}
      label={renderConfirmationInstructionLabel(confirmationInput.instruction, confirmationInput.instructionBoldText)}
      className="ds-modal-confirmation"
      errorText={confirmationMismatchMessage}
    >
      <Input
        id={resolvedConfirmationInputId}
        className="ds-modal-confirmation__input"
        value={confirmationValue}
        onChange={(e) => setConfirmationValue(e.target.value)}
        onKeyDown={handleConfirmationKeyDown}
        placeholder={confirmationInput.placeholder ?? ''}
        autoComplete="off"
        aria-invalid={confirmationHasMismatch}
        error={Boolean(confirmationMismatchMessage)}
      />
    </FormField>
  ) : null;

  const confirmationBlock =
    confirmationField && confirmationInput.confirmationPanel ? (
      <div className="ds-modal-confirmation-panel">{confirmationField}</div>
    ) : (
      confirmationField
    );

  const renderLegacyDefault = () => (
    <>
      {showIcon && (
        <FlexRow className="ds-modal-icon" align="center" justify="center" fullWidth aria-hidden="true">
          {icon !== undefined ? icon : renderLegacyDefaultIcon(type)}
        </FlexRow>
      )}

      {title ? (
        <Typography id={titleId} variant="h3" className="ds-modal-title">
          {title}
        </Typography>
      ) : null}

      {description ? (
        <Typography variant="body1" className="ds-modal-description">
          {description}
        </Typography>
      ) : null}

      {confirmationBlock}
      {renderActions()}
    </>
  );

  const renderModuleDefault = () => (
    <FlexColumn className="ds-modal__module-inner">
      <FlexColumn className="ds-modal__module-leading">
        {showIcon && (
          <FlexRow
            className={classNames('ds-modal__badge', type === 'danger' && 'ds-modal__badge--danger')}
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden="true"
          >
            <FlexRow className="ds-modal__badge-icon" align="center" justify="center" inline fullWidth={false}>
              {icon !== undefined ? icon : renderModuleBadgeIcon(type)}
            </FlexRow>
          </FlexRow>
        )}
        <FlexColumn className="ds-modal__text-stack">
          {title ? (
            <Typography id={titleId} component="h2" variant="headingMedium" className="ds-modal__heading">
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography component="p" variant="t17" className="ds-modal__detail">
              {description}
            </Typography>
          ) : null}
        </FlexColumn>
      </FlexColumn>
      {confirmationBlock}
      {renderActions()}
    </FlexColumn>
  );

  const showDangerBanner = Boolean(dangerBanner?.title?.trim());

  const renderDialogDangerBanner = () => {
    if (!showDangerBanner) return null;
    return (
      <FlexRow className="ds-modal__danger-banner" align="start" justify="between" fullWidth>
        <FlexRow className="ds-modal__danger-banner__leading" align="start" gap={12}>
          <FlexRow
            className="ds-modal__danger-banner__icon-wrap"
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            <AlertIcon size={24} fill="var(--pal-red-600)" stroke="var(--pal-red-600)" aria-hidden />
          </FlexRow>
          <FlexColumn className="ds-modal__danger-banner__text" gap={4} align="start">
            <Typography
              id={dangerBannerTitleId}
              component="h2"
              variant="headingMedium"
              className="ds-modal__danger-banner__title"
            >
              {dangerBanner.title}
            </Typography>
            {dangerBanner.subtitle?.trim() ? (
              <Typography component="p" variant="bodyMedium" className="ds-modal__danger-banner__subtitle">
                {dangerBanner.subtitle}
              </Typography>
            ) : null}
          </FlexColumn>
        </FlexRow>
        <Button
          id={resolvedCloseButtonId ? `${resolvedCloseButtonId}-banner` : undefined}
          variant="link"
          size="sm"
          type="button"
          className="ds-modal__danger-banner__close"
          icon={<CloseIcon size={20} strokeWidth={1.67} />}
          onClick={handleDangerBannerDismiss}
          aria-label="Close modal"
        />
      </FlexRow>
    );
  };

  const renderDialogDefault = () => (
    <FlexColumn
      className={classNames('ds-modal__dialog-inner', showDangerBanner && 'ds-modal__dialog-inner--danger-banner')}
    >
      {renderDialogDangerBanner()}
      <FlexColumn className="ds-modal__dialog-main">
        {!showDangerBanner ? (
          <FlexRow className="ds-modal__dialog-header" align="center" justify="between" fullWidth>
            {title ? (
              <Typography
                id={titleId}
                component="h2"
                variant="headingMedium"
                className="ds-modal__heading ds-modal__heading--dialog"
              >
                {title}
              </Typography>
            ) : (
              <FlexColumn
                aria-hidden
                className="ds-modal__dialog-header-placeholder"
                style={{ flex: '1 1 auto', minWidth: 0 }}
              />
            )}
            {showClose ? (
              <Button
                id={resolvedCloseButtonId}
                variant="link"
                size="sm"
                type="button"
                className="ds-modal__dialog-header-close"
                icon={<CloseIcon size={24} strokeWidth={1.5} />}
                onClick={handleDismiss}
                aria-label="Close modal"
              />
            ) : null}
          </FlexRow>
        ) : null}
        {description ? (
          <Typography component="p" variant="bodyMedium" className="ds-modal__detail ds-modal__detail--dialog">
            {description}
          </Typography>
        ) : null}
      </FlexColumn>
      {confirmationBlock}
      {renderActions()}
    </FlexColumn>
  );

  const renderDefaultShell = () => {
    if (variant === 'module') return renderModuleDefault();
    if (variant === 'dialog') return renderDialogDefault();
    return renderLegacyDefault();
  };

  return ReactDOM.createPortal(
    <FlexRow className={classNames('ds-modal-overlay', overlayClassName)} align="center" justify="center" fullWidth>
      {onDismiss && (
        <button type="button" className="ds-modal-backdrop" aria-label="Close dialog" onMouseDown={handleDismiss} />
      )}
      <FlexColumn
        id={id}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={(() => {
          if (composed) return undefined;
          if (variant === 'dialog' && showDangerBanner) return dangerBannerTitleId;
          if (title) return titleId;
          return undefined;
        })()}
      >
        {showClose && variant !== 'dialog' ? (
          <Button
            id={resolvedCloseButtonId}
            variant="link"
            size="sm"
            type="button"
            className="ds-modal-close"
            icon={<CloseIcon size={16} strokeWidth={1.5} />}
            onClick={handleDismiss}
            aria-label="Close modal"
          />
        ) : null}

        {composed && variant === 'dialog' && showClose ? (
          <FlexRow className="ds-modal__dialog-composed-header" justify="end" fullWidth>
            <Button
              id={resolvedCloseButtonId}
              variant="link"
              size="sm"
              type="button"
              className="ds-modal__dialog-header-close"
              icon={<CloseIcon size={24} strokeWidth={1.5} />}
              onClick={handleDismiss}
              aria-label="Close modal"
            />
          </FlexRow>
        ) : null}

        {composed ? children : renderDefaultShell()}
      </FlexColumn>
    </FlexRow>,
    document.body
  );
};

Modal.propTypes = {
  open: PropTypes.bool,
  variant: PropTypes.oneOf(['legacy', 'module', 'dialog']),
  type: PropTypes.oneOf(['success', 'danger']),
  centered: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  primaryText: PropTypes.string,
  secondaryText: PropTypes.string,
  onPrimary: PropTypes.func,
  /** Called when the secondary action is pressed; defaults to `onDismiss` when omitted. */
  onSecondary: PropTypes.func,
  onDismiss: PropTypes.func,
  showClose: PropTypes.bool,
  wideButtons: PropTypes.bool,
  grayFooter: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  id: PropTypes.string,
  primaryButtonId: PropTypes.string,
  secondaryButtonId: PropTypes.string,
  confirmationInputId: PropTypes.string,
  showIcon: PropTypes.bool,
  icon: PropTypes.node,
  confirmationInput: PropTypes.shape({
    instruction: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** Substring in `instruction` to render semibold; omit for plain label text. */
    instructionBoldText: PropTypes.string,
    /** When true, wraps the instruction + input in a muted panel (dialog / module / legacy). */
    confirmationPanel: PropTypes.bool,
    placeholder: PropTypes.string,
    expectedValue: PropTypes.string.isRequired,
    ignoreCase: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  primaryButtonTone: PropTypes.oneOf(['primary', 'secondary']),
  dangerBanner: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
  }),
  onDangerBannerDismiss: PropTypes.func,
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export { Modal };
export default Modal;
